import React, {useEffect, useState} from "react";
import {Modal, ModalBody, ModalHeader} from "../../../components/Modal";
import CustomButton from "../../../components/CustomButton";
import TwoBars from "../../../components/Bars/TwoBars";
import iconInfo from "url:shared-assets/icons/white/information-circle.svg";
import profileIcon from "url:shared-assets/icons/profile.svg";
import SurveyBadge from "../../../components/Badges/SurveyBadge";
import QuestionBadge from "../../../components/Badges/QuestionBadge";
import ScoreBadge from "../../../components/Badges/ScoreBadge";
import {AiFocusArea, FeedbackType, SurveyQuestionInterface, SurveyResultInterface} from "../interfaces";
import { useTranslation } from "react-i18next";
import {translateQuestion} from "../translations_name";

interface Props {
  clientName: string;
  questions: Array<SurveyQuestionInterface>;
  surveyResult: SurveyResultInterface;
  aiFocusArea?: AiFocusArea;
  feedbackType: FeedbackType;
  lang: string;
}
const SurveyQuestionsResult = (props: Props) => {

  const [viewQuestion, setViewQuestion] = useState("all");
  const [answerOptions, setAnswerOptions] = useState<Array<string>>([]);
  const [noAnswerInfo, setNoAnswerInfo] = useState(false);
  const translation = useTranslation();
  const { t } = translation;

  useEffect(() => {
    setAnswerOptions(calculateAnswerOptions());
  }, [props.lang]);

  const calculateAnswerOptions = () => {
    let selectedLang = props.lang;
    if (props.surveyResult.languages.indexOf(props.lang) === -1) {
      selectedLang = props.surveyResult.languages[0];
    }
    return props.surveyResult.answerOptions[selectedLang];
  }

  const findQuestion = () => {
    for (const question of props.questions) {
      if (question.id === viewQuestion) return question;
    }
    return null;
  }

  const validatedText = (scoreSelf: number, scoreOther: number) => {
    if (scoreOther >= 4) {
      if (scoreSelf >= 4) {
        return t("assessment-question-interpretation-validated-strength");
      } else {
        return t("assessment-question-interpretation-hidden-strength");
      }
    } else {
      if (scoreSelf >= 4) {
        return t("assessment-question-interpretation-blind-spot");
      } else {
        return t("assessment-question-interpretation-growth-priority");
      }
    }
  }

  const spreadText = (spread: number) => {
    if (spread >= 0.7) {
      return t("feedback-alignment-healthy");
    } else if (spread >= 0.4) {
      return t("feedback-alignment-balanced");
    } else if (spread >= 0.1) {
      return t("feedback-alignment-moderate");
    } else if (spread >= 0) {
      return t("feedback-alignment-low");
    } else {
      return "N/A (too few responses)"
    }
  }

  const renderQuestions = () => {

    if (viewQuestion === "all") {
      return (
        <>
          {
            props.questions.map((question, i) => {
              const badgeType = props.feedbackType === FeedbackType.ONLY_SELF ? "self_only" : "validated";

              return (
                <div className="bg-indigo-800 rounded-[6px] p-4 mt-4 border-indigo-700 border-1" key={"question_" + question.id}>
                  <div className="flex gap-x-4">
                    <div className="z-badge transparent">#{i + 1}</div>
                    <SurveyBadge lang={props.lang} type={badgeType} max={props.surveyResult.nrAnswerOptions}
                                 value={question.scoreSelf}
                                 value2={question.scoreOther}/>
                  </div>
                  <div className="flex flex-col md:flex-row mt-2">
                    <div className="md:w-1/2">{translateQuestion(props.lang, question, props.clientName)}</div>
                    <div className="md:w-1/2 md:pl-4 pt-2 md:pt-0">
                      <TwoBars value1={question.scoreSelf} value2={question.scoreOther} max={props.surveyResult.nrAnswerOptions} size="small"/>
                      <div className="mt-2 flex">
                        <div className="text-sm">
                          {t("self-assessment")}: <span
                          className="z-title2">{question.scoreSelf.toFixed(1)}</span>/{props.surveyResult.nrAnswerOptions}
                        </div>
                        <div className="ml-4 text-sm">
                          Feedback: <span
                          className="z-title2">{props.feedbackType === FeedbackType.ONLY_SELF ? "-" : question.scoreOther.toFixed(1)}</span>/{props.surveyResult.nrAnswerOptions}
                        </div>
                        <div className="ml-4 text-sm">
                          Gap: <span
                          className="z-title2">{props.feedbackType === FeedbackType.ONLY_SELF ? "-" : Math.abs(question.scoreOther - question.scoreSelf).toFixed(1)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )
            })
          }
        </>
      )
    } else {
      const question = findQuestion();
      if (!question) return <>Can't find question {viewQuestion}</>
      const scoreList = [...question.scoreList].reverse();
      return (
        <div className="bg-indigo-800 rounded-[6px] p-4 mt-4 border-indigo-700 border-1">
          <p>{question.name}</p>
          <div className="flex gap-x-4 flex-col md:flex-row items-stretch">
            <div className="border border-indigo-700 rounded-[6px] p-4 mt-4 md:w-1/2">
              <p className="font-medium">{t("answers")}</p>
              <div className="w-full flex flex-col gap-4 flex-1">
                {scoreList.map((responses, idx) => {
                  const score = props.surveyResult.nrAnswerOptions - idx;
                  const option = answerOptions[score];
                  // Calculate percentage for this answer
                  const percentage =
                    responses > 0 ? (responses / props.surveyResult.nrOtherResponses) * 100 : 0;

                  return (
                    <div key={`answer-${idx}`} className="">
                      {/* Label and Value Section */}
                      <div className="flex justify-between items-center">
                        <div className="flex items-center">
                          <div className="z-title3 text-gray-400">
                            {option}
                          </div>
                          {idx === props.surveyResult.nrAnswerOptions && (
                            <div className="ml-2">
                              <CustomButton
                                buttonType="transparentBase"
                                size="sm"
                                isRound
                                leftIcon={<img src={iconInfo} alt="info"/>}
                                onClick={() => setNoAnswerInfo(true)}
                              />
                            </div>
                          )}
                        </div>

                        <div className="flex items-center">
                          {score === question.scoreSelf && (
                            <div className="mr-2">
                              <ScoreBadge
                                txt={t("you")}
                                type="focus_area"
                                size="sm"
                                self={true}
                              />
                            </div>
                          )}
                          <img
                            src={profileIcon}
                            alt="profile icon"
                            className="w-4 h-4"
                          />
                          <span className="ml-2 text-white font-medium">
                              {responses}
                            </span>
                        </div>
                      </div>
                      {/* Bar Section */}
                      <div className="w-full bg-indigo-700 rounded-full mt-1 w-1/2 items-center">
                        <div
                          className={"bg-peach-300 h-2 rounded-full"}
                          style={{width: `${percentage}%`}}
                        >
                          {/*{percentage > 0 && (*/}
                          {/*  <div className="flex justify-end items-center text-black text-xs font-medium pr-1">*/}
                          {/*    {percentage.toFixed(0)}%*/}
                          {/*  </div>*/}
                          {/*)}*/}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="mt-4 md:w-1/2 flex flex-col">
              <div className="border border-indigo-700 rounded-[6px] p-4 mb-2">
                <p className="z-title3">{props.lang === "sv" ? "Snittpoäng" : "Average scores"}</p>
                <div className="mt-1 flex pb-5">
                  <div className="text-sm">
                    {t("self-assessment")}: <span
                    className="z-title2">{question.scoreSelf.toFixed(1)}</span>/{props.surveyResult.nrAnswerOptions}
                  </div>
                  <div className="ml-4 text-sm ">
                    Feedback: <span
                    className="z-title2">{question.scoreOther.toFixed(1)}</span>/{props.surveyResult.nrAnswerOptions}
                  </div>
                  <div className="ml-4 text-sm">
                    Gap: <span
                    className="z-title2">{Math.abs(question.scoreOther - question.scoreSelf).toFixed(1)}</span>
                  </div>
                </div>
              </div>

              <div className="border border-indigo-700 rounded-[6px] p-4 mt-2 mb-2">
                <div className="flex justify-between items-center">
                  <p className="z-title3">{props.lang === "sv" ? "Tolkning" : "Interpretation"}</p>
                  <SurveyBadge lang={props.lang} type="validated" max={props.surveyResult.nrAnswerOptions}
                               value={question.scoreSelf}
                               value2={question.scoreOther}/>
                </div>

                <div className="mt-2 pb-5">
                  {validatedText(question.scoreSelf, question.scoreOther)}
                </div>
              </div>

              <div className="border border-indigo-700 rounded-[6px] p-4 mt-2">
                <div className="flex justify-between items-center">
                  <p className="z-title3">{props.lang === "sv" ? "Enighet i feedback" : "Feedback alignment"}</p>
                  <SurveyBadge lang={props.lang} type="feedback_alignment"
                               value={question.spreadRespondentsNormalized}/>
                </div>

                <div className="mt-2 pb-5">
                  {spreadText(question.spreadRespondentsNormalized)}
                </div>

              </div>
            </div>

          </div>


            <div className="mt-4 border border-indigo-700 rounded-md p-4">
                <div className="z-title3 text-gray-100">
                  {props.lang === "sv" ? "Kommentarer" : "Comments"}
                </div>
              {question.freeTexts.length > 0 ? (
                <div>
                  {
                question.freeTexts.map((freeText, i) => {
                  return (
                    <div key={"free_" + i} className="mt-1 text-sm text-gray-300 whitespace-pre-line">
                      &quot;{freeText}&quot;
                    </div>
                  )
                })
              }
                </div>
              ):(
                <div className="mt-1 text-sm text-gray-300 whitespace-pre-line">
                      {t("no-comment-text")}
                </div>
              )}
            </div>

          <Modal isOpen={noAnswerInfo} onClose={() => setNoAnswerInfo(false)}>
            <ModalHeader onClose={() => setNoAnswerInfo(false)}>
              <div className="z-title1">{t("why-no-answer")}</div>
            </ModalHeader>
            <ModalBody>
              <div className="font-medium">{t("why-no-answer-1")}</div>
              <div className="mt-1">{t("why-no-answer-2")}</div>

              <div className="font-medium mt-4">{t("why-no-answer-3")}</div>
              <div className="mt-1">{t("why-no-answer-4")}</div>

              <div className="font-medium mt-4">{t("why-no-answer-5")}</div>
              <div className="mt-1">{t("why-no-answer-6")}</div>
            </ModalBody>
          </Modal>
        </div>
      )
    }
  }

  return (
    <div className="mt-4">
      {
        props.aiFocusArea &&
          <div className="flex gap-x-4 mt-4">
              <div className="border border-indigo-700 rounded-sm p-4 w-1/2">
                  <div className="font-medijm">{props.lang === "sv" ? "Insikter" : "Insights"}</div>
                  <div className="text-gray-400 mt-1 whitespace-pre-line">
                    {props.aiFocusArea.insights}
                  </div>
              </div>

              <div className="border border-indigo-700 rounded-sm p-4 w-1/2">
                  <div className="font-medijm">{props.lang === "sv" ? "Områdets status" : "Area Health"}</div>
                  <div className="text-gray-400 mt-1 whitespace-pre-line">
                    {props.aiFocusArea.health}
                  </div>
              </div>
          </div>
      }
      {
        props.feedbackType === FeedbackType.FEEDBACK &&
          <div className="flex flex-wrap mt-6">
              <QuestionBadge id="all" text={t("all-questions")} selectedId={viewQuestion}
                             clickId={setViewQuestion}/>
            {
              props.questions.map((question, i) => (
                <QuestionBadge id={question.id} text={t("question") + " " + (i + 1)} selectedId={viewQuestion}
                               clickId={setViewQuestion} key={"qapa_" + question.id}/>
              ))
            }
          </div>
      }


      {renderQuestions()}
    </div>
  )
}

export default SurveyQuestionsResult;
